import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";

import { axios } from "@/client/libs/axios";

export type CoverLetter = {
  id: string;
  content: string;
  resumeId: string;
  createdAt: string;
  updatedAt: string;
};

type CreateCoverLetterRequest = {
  content?: string;
  resumeId: string;
};

type UpdateCoverLetterRequest = {
  content: string;
};

export const createCoverLetter = async (data: CreateCoverLetterRequest): Promise<CoverLetter> => {
  const response = await axios.post<
    CoverLetter,
    AxiosResponse<CoverLetter>,
    CreateCoverLetterRequest
  >("/cover-letter", data);

  return response.data;
};

export const getCoverLetter = async (resumeId: string): Promise<CoverLetter | null> => {
  try {
    const response = await axios.get<CoverLetter>(`/cover-letter/resume/${resumeId}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

export const updateCoverLetter = async (
  resumeId: string,
  data: UpdateCoverLetterRequest,
): Promise<CoverLetter> => {
  const response = await axios.put<
    CoverLetter,
    AxiosResponse<CoverLetter>,
    UpdateCoverLetterRequest
  >(`/cover-letter/resume/${resumeId}`, data);

  return response.data;
};

export const deleteCoverLetter = async (resumeId: string): Promise<void> => {
  await axios.delete(`/cover-letter/resume/${resumeId}`);
};

// Hooks
export const useCoverLetter = (resumeId: string) => {
  return useQuery({
    queryKey: ["cover-letter", resumeId],
    queryFn: () => getCoverLetter(resumeId),
    enabled: !!resumeId,
  });
};

export const useCreateCoverLetter = () => {
  const queryClient = useQueryClient();

  const {
    error,
    isPending: loading,
    mutateAsync: createCoverLetterFn,
  } = useMutation({
    mutationFn: createCoverLetter,
    onSuccess: (data) => {
      queryClient.setQueryData(["cover-letter", data.resumeId], data);
      queryClient.invalidateQueries({ queryKey: ["resumes"] });
    },
  });

  return { createCoverLetter: createCoverLetterFn, loading, error };
};

export const useUpdateCoverLetter = () => {
  const queryClient = useQueryClient();

  const {
    error,
    isPending: loading,
    mutateAsync: updateCoverLetterFn,
  } = useMutation({
    mutationFn: ({ resumeId, data }: { resumeId: string; data: UpdateCoverLetterRequest }) =>
      updateCoverLetter(resumeId, data),
    onSuccess: (data) => {
      queryClient.setQueryData(["cover-letter", data.resumeId], data);
      queryClient.invalidateQueries({ queryKey: ["resumes"] });
    },
  });

  return { updateCoverLetter: updateCoverLetterFn, loading, error };
};

export const useDeleteCoverLetter = () => {
  const queryClient = useQueryClient();

  const {
    error,
    isPending: loading,
    mutateAsync: deleteCoverLetterFn,
  } = useMutation({
    mutationFn: deleteCoverLetter,
    onSuccess: (_, resumeId) => {
      queryClient.setQueryData(["cover-letter", resumeId], null);
      queryClient.invalidateQueries({ queryKey: ["resumes"] });
    },
  });

  return { deleteCoverLetter: deleteCoverLetterFn, loading, error };
};
